import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { X, Star, MessageCircle, Lock, Calendar, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const FeedbackColloquioModal = ({ 
  isOpen, 
  onClose, 
  colloquioId, 
  userType, // 'agenzia' o 'candidato'
  colloquioData 
}) => {
  const [formData, setFormData] = useState({
    valutazione: 5,
    feedback: "",
    note: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [existingFeedback, setExistingFeedback] = useState(null);
  const [isLocked, setIsLocked] = useState(false);

  // Carica feedback esistente all'apertura
  useEffect(() => {
    if (isOpen && colloquioId) {
      loadExistingFeedback();
    }
  }, [isOpen, colloquioId]);

  const loadExistingFeedback = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = userType === 'agenzia' 
        ? `/api/v1/agenzie/colloquio/${colloquioId}/feedback`
        : `/api/v1/candidato/colloquio/${colloquioId}/feedback`;
      
      const response = await axios.get(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;
      setExistingFeedback(data);
      setIsLocked(data.is_locked);

      if (data.valutazione) {
        setFormData({
          valutazione: data.valutazione || 5,
          feedback: data.feedback || "",
          note: data.note || ""
        });
      }
    } catch (error) {
      console.error("Errore caricamento feedback:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Rimosso controllo obbligatorietà - feedback ora opzionale
    
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      const endpoint = userType === 'agenzia' 
        ? `/api/v1/agenzie/colloquio/${colloquioId}/feedback`
        : `/api/v1/candidato/colloquio/${colloquioId}/feedback`;

      await axios.post(`${API}${endpoint}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      toast.success("Feedback inviato con successo!");
      setIsLocked(true);
      onClose(true);
      
    } catch (error) {
      console.error("Error submitting feedback:", error);
      
      // Gestione errori di validazione
      let errorMessage = "Errore nell'invio del feedback";
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Se detail è un array (errori di validazione Pydantic)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } 
        // Se detail è una stringa
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Se detail è un oggetto
        else {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (rating, onRatingChange) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={isLocked}
            onClick={() => onRatingChange && onRatingChange(star)}
            className={`p-1 ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-110'} transition-transform`}
          >
            <Star
              className={`w-6 h-6 ${
                star <= rating
                  ? 'text-yellow-400 fill-yellow-400'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
        <span className="ml-2 text-sm text-gray-600">
          {rating}/5
        </span>
      </div>
    );
  };

  if (!isOpen) return null;
  // Prevent background scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" style={{overflow: 'hidden'}}>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100 flex flex-col" style={{minHeight: '400px'}}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isLocked ? 'Visualizza Feedback' : 'Lascia Feedback'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {colloquioData?.stato || 'N/A'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="p-6 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-sm text-gray-600">Caricamento...</p>
          </div>
        )}

        {/* Content */}
        {!isLoading && (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Lock Notice */}
            {isLocked && (
              <div className="flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <Lock className="w-4 h-4 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    Feedback già inviato
                  </p>
                  <p className="text-xs text-blue-600">
                    Il feedback è stato inviato il {new Date(existingFeedback?.data_feedback).toLocaleDateString('it-IT')} e non può essere modificato
                  </p>
                </div>
              </div>
            )}

            {/* Valutazione */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Valutazione
              </label>
              {renderStars(formData.valutazione, isLocked ? null : (rating) => 
                setFormData(prev => ({ ...prev, valutazione: rating }))
              )}
            </div>

            {/* Feedback testuale */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Feedback dettagliato
              </label>
              <textarea
                value={formData.feedback}
                onChange={(e) => setFormData(prev => ({ ...prev, feedback: e.target.value }))}
                disabled={isLocked}
                className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                  isLocked ? 'bg-gray-50 cursor-not-allowed' : ''
                }`}
                rows={4}
                placeholder={userType === 'agenzia' 
                  ? "Descrivi la tua esperienza con questo candidato..." 
                  : "Descrivi la tua esperienza con questa agenzia..."
                }
                maxLength={500}
              />
              <div className="flex justify-end items-center mt-1">
                <p className="text-xs text-gray-500">
                  {formData.feedback.length}/500
                </p>
              </div>
            </div>

            {/* Note aggiuntive */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note aggiuntive
              </label>
              <textarea
                value={formData.note}
                onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
                disabled={isLocked}
                className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                  isLocked ? 'bg-gray-50 cursor-not-allowed' : ''
                }`}
                rows={2}
                placeholder="Note opzionali..."
                maxLength={200}
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.note.length}/200
              </p>
            </div>

            {/* Actions */}
            {!isLocked && (
              <div className="flex space-x-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Invio...
                    </>
                  ) : (
                    <>
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Invia Feedback
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Chiudi se locked */}
            {isLocked && (
              <div className="flex justify-center pt-4 border-t">
                <Button onClick={onClose} variant="outline" className="w-full">
                  Chiudi
                </Button>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default FeedbackColloquioModal;